/**
 * In-memory JWT store. The closure-captured `jwt` variable is the single
 * source of truth. PRD-002 DoD requires the JWT to live ONLY in
 * application memory — never localStorage, sessionStorage, cookies, or
 * IndexedDB. Closing the tab or refreshing discards the token.
 *
 * The store exposes a `subscribe` API so React components can re-render
 * via `useSyncExternalStore`. It also notifies listeners synchronously
 * to make logout-driven UI transitions immediate.
 */

let jwt: string | null = null;
const listeners = new Set<() => void>();

function notify() {
    for (const listener of listeners) listener();
}

export const jwtStore = {
    get(): string | null {
        return jwt;
    },
    set(newJwt: string): void {
        jwt = newJwt;
        notify();
    },
    clear(): void {
        jwt = null;
        notify();
    },
    subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },
};

/**
 * Test-only reset. Used by Vitest setup to ensure a clean state between tests.
 */
export function __resetJwtStoreForTests(): void {
    jwt = null;
    listeners.clear();
}
